import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { forgotPassword, setAuthError } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MyTextField from '../components/MyTextField';
import MySnackbar from '../components/MySnackbar';
import LoadingComponent from '../components/LoadingComponent';
import s10Logo from '../assets/images/s10Logo.png';

const ForgotPassword = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isLoading, error } = useSelector((state: RootState) => state.auth);
    
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [emailSent, setEmailSent] = useState(false);

    const validateEmail = (): boolean => {
        if (!email.trim()) {
            setEmailError('El email es requerido');
            return false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError('Ingresa un email válido');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateEmail()) return;

        const result = await dispatch(forgotPassword(email));
        if (forgotPassword.fulfilled.match(result)) {
            setEmailSent(true);
        }
    };

    if (emailSent) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh', backgroundColor: theme.s10palette?.S10Blueblue50 }}>
                <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
                <img src={s10Logo} alt="S10" style={{ width: '8.75rem', marginBottom: '2rem' }} />
                <Typography variant="h5" gutterBottom>
                    Correo enviado
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Hemos enviado un código de verificación a <strong>{email}</strong>. Revisa tu bandeja de entrada.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/reset-password', { state: { email } })}
                    sx={{
                        backgroundColor: theme.s10palette?.S10Blueblue600,
                        '&:hover': { backgroundColor: theme.s10palette?.S10Blueblue700 },
                    }}
                >
                    Continuar
                </Button>
                </Paper>
            </Grid>
        );
    }

  return (
    <Grid container style={{ height: "100vh", margin: "0 !important" }}>
        <Grid
            container
            item
            xs={12}
            md={6}
            direction="column"
            justifyContent="center"
            alignItems="center"
            style={{ backgroundColor: theme.s10palette?.S10Blueblue50 }}
        >
            <MySnackbar
                open={!!error}
                autoHideDuration={6000}
                msg={error || ''}
                severity="error"
                onClose={() => dispatch(setAuthError(''))}
            />

            <Grid
                container
                direction="row"
                maxWidth="20rem"
                alignItems="center"
                rowSpacing="1rem"
                component="form"
                onSubmit={handleSubmit}
                spacing={2}
            >
                <Grid justifyContent="center" alignItems="center" alignContent="center" item xs={12} style={{ marginTop: ".5rem" }}>
                    <img style={{ width: "8.75rem" }} src={s10Logo} alt="S10" />
                </Grid>
                <Typography variant="h6">Portal Mis Boletas - Olvidó contraseña</Typography>
                <Divider
                    style={{
                    width: "100%",
                    marginTop: "1.19rem",
                    marginBottom: "1.5rem",
                    color: theme.s10palette.S10Blueblue100,
                    }}
                />
                <Typography variant="body1" align="left" width="100%">
                    Ingresa tus credenciales
                </Typography>
                <MyTextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                    fullWidth
                />

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate('/login')}
                            sx={{ borderColor: theme.s10palette?.S10Greygrey300 }}
                        >
                            Cancelar
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={isLoading}
                            sx={{
                            backgroundColor: theme.s10palette?.S10Blueblue600,
                            '&:hover': { backgroundColor: theme.s10palette?.S10Blueblue700 },
                            }}
                        >
                            Enviar
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>

        <Grid
            item
            md={6}
            sx={{ display: { xs: 'none', md: 'block' } }}
            style={{
                backgroundColor: theme.s10palette?.S10Blueblue700,
                backgroundImage: `url(loginDefaultImage.svg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        />

        {isLoading && <LoadingComponent loadingText="Iniciando sesión..." />}
    </Grid>
  );
};

export default ForgotPassword;